import type { NoteDetail } from '@/api/notes';
import { bytesToStr, decryptWithWrappedKey, type KeyPair } from '@/crypto/sodium';
import { readDraft } from '@/features/notes/noteDraft';
import { isDraftNewerThanServer } from '@/features/notes/editor/draftDecisions';

export interface LoadedNoteContent {
  /** HTML-ul decriptat de pe server — referința pentru dirty diff. */
  serverHtml: string;
  /** Ce se pune în editor: ciorna, dacă e mai nouă, altfel `serverHtml`. */
  initialHtml: string;
  initialTitle: string;
  /** true dacă `initialHtml`/`initialTitle` vin din ciorna locală. */
  restoredDraft: boolean;
}

/**
 * Decriptează notița și decide dacă pornim de la ciorna locală.
 * Aruncă dacă decriptarea eșuează — apelantul afișează eroarea.
 */
export async function loadNoteContent(args: {
  note: NoteDetail;
  keyPair: KeyPair;
  userId: string | undefined;
  noteId: string | null;
}): Promise<LoadedNoteContent> {
  const { note, keyPair, userId, noteId } = args;
  const plainBytes = await decryptWithWrappedKey(note, keyPair);
  const serverHtml = bytesToStr(plainBytes);

  // Dacă există un draft local mai nou, prefer-îl (user încă nu a
  // salvat server-side). Altfel folosim ce a venit din server.
  const draft = userId !== undefined && noteId ? await readDraft(userId, noteId) : null;
  if (draft !== null && isDraftNewerThanServer(draft.savedAt, note.updated_at)) {
    return {
      serverHtml,
      initialHtml: draft.html,
      initialTitle: draft.title,
      restoredDraft: true,
    };
  }
  return {
    serverHtml,
    initialHtml: serverHtml,
    initialTitle: note.title,
    restoredDraft: false,
  };
}
